import React, { useCallback } from 'react'
import { EvidenceGroup } from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  useCanPerformAction,
  useHasRequiredPermissions,
} from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { DropdownToggleButton } from 'src/components/Reusable/Buttons'
import { Dropdown } from 'react-bootstrap'
import { PERMISSIONS } from 'src/config/roleConfig'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import { useNavigate, useParams } from 'react-router-dom'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { useGrpcRevalidateByMethod } from 'src/components/async'
import { RequestPromiseClient } from '@trustero/trustero-api-web/lib/request/request_grpc_web_pb'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import { useUnlinkEvidenceFromRequest } from 'src/components/ModalForms/Evidence/useDeleteEvidence'
import { ReactComponent as VerticalEllipsesIcon } from 'src/components/Icons/assets/vertical-ellipses-icon.svg'
import { useDeleteRequestModal } from 'src/pages/Requests/DeleteRequestModal/DeleteRequestModal'
import { DocumentRequest } from '@trustero/trustero-api-web/lib/request/request_pb'

export const ViewRequestDetails = ({
  requestId,
}: {
  requestId: string
}): JSX.Element => {
  const navigate = useNavigate()
  const { pageContext } = useParams()
  const onViewDetails = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      navigate(
        generatePermalink({
          pageContext: pageContext as string,
          modelId: requestId,
          modelType: MODEL_TYPE.REQUEST,
          isInternalLink: true,
        }),
      )
    },
    [requestId, navigate, pageContext],
  )

  return (
    <Dropdown.Item eventKey="1" as="button" onClick={onViewDetails}>
      View Details
    </Dropdown.Item>
  )
}

export const UnlinkRequest = ({
  caption,
  requestId,
  evidenceId,
}: {
  caption: string
  requestId: string
  evidenceId: string
}): JSX.Element => {
  const mutate = useGrpcRevalidateByMethod()
  const mutateFunc = async () => {
    await Promise.all([
      mutate(RequestPromiseClient.prototype.listDocumentRequests),
      mutate(AttachmentPromiseClient.prototype.getEvidenceControls),
    ])
  }
  const unlinkRequest = useUnlinkEvidenceFromRequest({
    evidenceId,
    requestId,
    caption,
    mutateFunc,
  })

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    unlinkRequest()
  }

  return (
    <Dropdown.Item eventKey="2" as="button" onClick={onClick}>
      Unlink Request
    </Dropdown.Item>
  )
}

export const CopyRequestLink = ({
  requestId,
}: {
  requestId: string
}): JSX.Element => {
  const { pageContext } = useParams()
  const onCopyLink = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault()
      await navigator.clipboard.writeText(
        generatePermalink({
          pageContext: pageContext as string,
          modelId: requestId,
          modelType: MODEL_TYPE.REQUEST,
          isInternalLink: false,
        }),
      )
    },
    [requestId, pageContext],
  )
  return (
    <Dropdown.Item eventKey="6" as="button" onClick={onCopyLink}>
      Copy Permalink
    </Dropdown.Item>
  )
}

export const DeleteRequest = ({
  request,
}: {
  request: DocumentRequest
}): JSX.Element => {
  const canDeleteRequest = useCanPerformAction<DocumentRequest.AsObject>(
    CAPABILITY.CAN_DELETE_REQUESTS,
    request.toObject(),
  )
  const deleteRequest = useDeleteRequestModal({ request })

  if (!canDeleteRequest) {
    return <></>
  }

  return (
    <Dropdown.Item
      eventKey="3"
      as="button"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        deleteRequest()
      }}
    >
      Delete Request
    </Dropdown.Item>
  )
}

export const EvidenceRequestsItemMenu = ({
  evidenceGroup,
  request,
}: {
  evidenceGroup: EvidenceGroup
  request: DocumentRequest
}): JSX.Element => {
  const hasPermissions = useHasRequiredPermissions([
    PERMISSIONS.READ,
    PERMISSIONS.EDIT,
  ])
  if (!hasPermissions) {
    return <></>
  }

  const requestId = request.getId()
  const caption = evidenceGroup.getCaption()
  const evidenceId = evidenceGroup.getEvidenceId()

  return (
    <Dropdown id={`evidence-requests-index-menu-${requestId}`}>
      <Dropdown.Toggle as={DropdownToggleButton}>
        <VerticalEllipsesIcon />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <ViewRequestDetails requestId={requestId} />
        <UnlinkRequest
          caption={caption}
          evidenceId={evidenceId}
          requestId={requestId}
        />
        <DeleteRequest request={request} />
        <CopyRequestLink requestId={requestId} />
      </Dropdown.Menu>
    </Dropdown>
  )
}
